<script>
  /* global Global */
  import Header from '../common/Header.svelte';
  import Footer from '../common/Footer.svelte';
  import Loading from '../common/Loading.svelte';
  import Error from '../common/Error.svelte';
  import SmallTitle from '../common/SmallTitle.svelte';
  import GameScoreLegend from '../ui-components/games/GameScoreLegend.svelte';
  import GameScore from '../ui-components/games/GameScore.svelte';
  import GameStatisticsPlayers from '../ui-components/games/GameStatisticsPlayers.svelte';
  import GameStatisticsPitchers from '../ui-components/games/GameStatisticsPitchers.svelte';
  import ButtonLink from '../common/ButtonLink.svelte';
  import GamePlays from '../ui-components/games/GamePlays.svelte';
  import GameOfficials from '../ui-components/games/GameOfficials.svelte';
  import Common from '../js/common.js';

  export let params;

  let lang;
  let mode;

  let user = JSON.parse(window.localStorage.getItem('user'));
  const selectedYear = Number(params?.year) || Global.years[0];

  let game;
  let visitor;
  let home;
  let field;
  let score;
  let statistics;
  let plays;
  let officials;

  let loading = true;
  let error;

  (async () => {
    // eslint-disable-next-line no-undef
    const response = await fetch(`${Common.BACKEND_URL}/games/get/${params.game}/${selectedYear}`);

    if (response.ok) {
      const data = await response.json();

      game = data.game.game;
      visitor = data.game.visitor;
      home = data.game.home;
      field = data.game.field;
      score = data.game.score;
      statistics = data.game.statistics;
      plays = data.game.plays;
      officials = data.game.officials;
      user = data.user;

      if (user) window.localStorage.setItem('user', JSON.stringify(data.user));
      else window.localStorage.removeItem('user');
    } else {
      if (response.status === Common.NOT_FOUND) window.location.href = './';
      else error = true;
    }

    loading = false;
  })();

  const i18n = {
    pageTitle: {
      cs: `Zápas #${Common.addZeros(params.game)} | ${Global.project.name.cs}`,
      en: `Game #${Common.addZeros(params.game)} | ${Global.project.name.en}`,
    },
    game: {
      cs: 'Zápas',
      en: 'Game',
    },
    photogallery: {
      cs: 'Fotogalerie',
      en: 'Photogallery',
    },
  };

  const years = Global.years[0];
</script>

<svelte:head>
  <title> {i18n.pageTitle[lang]} </title>
</svelte:head>

<Header page='games/{params.game}{selectedYear !== years[0] ? `/${selectedYear}` : ''}' bind:lang bind:user bind:mode/>

<Loading bind:display={loading} fullHeight={true}/>
<Error bind:display={error} fullHeight={true} bind:lang/>

{#if !loading && !error}
  <div class='games-game'>
    <div class='games-game-details'>
      <div class='games-game-details-date-and-time'>
        <div class='games-game-details-date-and-time-value'>
          {game.date} ({game.time})
        </div>
      </div>

      <div class='games-game-details-number'>
        {i18n.game[lang]} #{Common.addZeros(game.number)}
      </div>

      <div class='games-game-details-field'>
        <a href='./#/fields/{field.code}' class='games-game-details-field-value'>
          {field.city} ({field.shortcut.toUpperCase()})
        </a>
      </div>
    </div>

    <div class='games-game-score'>
      <div class='games-game-score-legend'>
        <GameScoreLegend innings={plays.length}/>
      </div>

      <GameScore team={visitor} value={score.visitor} plays={plays.map(plays => plays[0])} bind:lang/>
      <GameScore team={home} value={score.home} plays={plays.map(plays => plays[1])} bind:lang/>
    </div>

    <div class='games-game-content'>
      <div class='games-game-content-left'>
        <div class='games-game-content-left-statistics'>
          <div class='games-game-content-left-statistics-visitor'>
            <SmallTitle value={visitor.name[lang]} small={true}/>

            <div class='games-game-content-left-statistics-visitor-statistics'>
              <GameStatisticsPlayers value={statistics.visitor.players} bind:lang bind:mode/>

              <GameStatisticsPitchers value={statistics.visitor.pitchers} bind:lang bind:mode/>
            </div>
          </div>

          <div class='games-game-content-left-statistics-home'>
            <SmallTitle value={home.name[lang]} small={true}/>

            <div class='games-game-content-left-statistics-home-statistics'>
              <GameStatisticsPlayers value={statistics.home.players} bind:lang bind:mode/>

              <GameStatisticsPitchers value={statistics.home.pitchers} bind:lang bind:mode/>
            </div>
          </div>
        </div>

        <div class='games-game-content-left-actions'>
          <div class='games-game-content-left-actions-action'>
            <ButtonLink value='Play by play' link={game.playByPlay} icon='sports_baseball' color='green'/>
          </div>

          {#if game.stream}
            <div class='games-game-content-left-actions-action'>
              <ButtonLink value='Stream' link={game.stream} icon='live_tv' color='red'/>
            </div>
          {/if}

          {#if game.photogallery}
            <div class='games-game-content-left-actions-action'>
              <ButtonLink value={i18n.photogallery[lang]} link={game.photogallery} icon='photo_library' color='yellow'/>
            </div>
          {/if}
        </div>

        {#if game.highlights}
          <div class='games-game-content-left-highlights'>
            <iframe class='games-game-content-left-highlights-content' src='https://embed.staylive.tv/video/{game.highlights}?&autoplay=false' title='Game highlights' frameborder='0' allowfullscreen></iframe>
          </div>
        {/if}
      </div>

      <div class='games-game-content-right'>
        <div class='games-game-content-right-plays'>
          {#each plays as plays, inning}
            {#each plays as plays, half}
              <div class='games-game-content-right-plays-game-plays'>
                <SmallTitle value={`${lang !== 'en'
                  ? `${half ? 'Dohrávka' : 'Začátek'} ${inning + 1}. směny`
                  : `${half ? 'Bottom' : 'Top'} of ${inning + 1}${Common.getOrdinal(inning + 1)} inning`}
                `} small={true}/>

                <GamePlays plays={plays} top={!half} bot={half}/>
              </div>
            {/each}
          {/each}

          <div class='games-game-content-right-officials'>
            <GameOfficials value={officials} bind:lang bind:mode/>
          </div>
        </div>
      </div>
    </div>
  </div>
{/if}

<Footer bind:lang/>

<style>
  .games-game {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 40px;

    max-width: 1440px;

    padding: 40px 60px;

    margin: auto;
  }

  .games-game-details {
    display: flex;
    justify-content: space-between;

    margin-bottom: -20px;

    font-family: var(--font-secondary);

    line-height: 1.25;

    color: var(--grey-5);
  }

  .games-game-details-date-and-time {
    display: flex;
    justify-content: flex-start;
    flex-grow: 1;
    flex-basis: 0;
  }

  .games-game-details-field {
    display: flex;
    justify-content: flex-end;
    flex-grow: 1;
    flex-basis: 0;
  }

  .games-game-details-field-value {
    text-decoration: underline;
    text-decoration-thickness: 1.5px;
    text-decoration-color: transparent;

    color: inherit;

    transition: text-decoration-color 0.25s ease-in-out;
  }

  .games-game-details-field-value:hover {
    text-decoration-color: var(--grey-5);
  }

  .games-game-score {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .games-game-score-legend {
    margin-bottom: -5px;
  }

  .games-game-content {
    display: flex;
    gap: 40px;
  }

  .games-game-content-left {
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    gap: 20px;

    width: calc((100% - 40px) / 2)
  }

  .games-game-content-left-statistics {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  .games-game-content-left-statistics-visitor,
  .games-game-content-left-statistics-home {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .games-game-content-left-statistics-visitor-statistics,
  .games-game-content-left-statistics-home-statistics {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  .games-game-content-left-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
  }

  .games-game-content-left-actions-action {
    display: flex;
    flex-grow: 1;

    width: calc((100% - 20px) / 2);
  }

  .games-game-content-left-highlights-content {
    width: 100%;

    border-radius: var(--radius-lg);

    background: var(--grey-1);

    aspect-ratio: 16 / 9;
  }

  .games-game-content-right {
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    gap: 20px;

    width: calc((100% - 40px) / 2);
  }

  .games-game-content-right-plays {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  .games-game-content-right-plays-game-plays {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  :global(body.dark) .games-game-details {
    color: var(--grey-6);
  }

  :global(body.dark) .games-game-details-field-value:hover {
    text-decoration-color: var(--grey-6);
  }

  @media only screen and (max-width: 1000px) {
    .games-game-content {
      flex-direction: column;
    }

    .games-game-content-left,
    .games-game-content-right {
      width: 100%;
    }
  }

  @media only screen and (max-width: 900px) {
    .games-game {
      padding: 40px;
    }
  }

  @media only screen and (max-width: 600px) {
    .games-game-details-date-and-time {
      flex-grow: 0;
      flex-basis: max-content;
    }

    .games-game-details-number {
      display: none;
    }

    .games-game-details-field {
      flex-grow: 0;
      flex-basis: max-content;
    }

    .games-game-content-left-actions-action {
      width: 100%;
    }
  }

  @media only screen and (max-width: 350px) {
    .games-game {
      padding: 40px 20px;
    }
  }
</style>
